import { useReducer } from 'react';
import { notify } from 'legacy-packages/ft-cb';
import { toast } from 'chameleon/ui-stack/ui/toast';
import log from 'chameleon/ui-stack/utilities/log';
import { saveAuthorInstance } from 'legacy/src/components/learnosity/utils/helpers';

import { mapSectionsToQuestions } from './EditScoring/scoringHelpers';

export const publishReducer = (state, { type, payload = {} }) => {
  const { navigateToAfterPublishing } = payload;
  switch (type) {
    case 'startPublishing':
      return { ...state, publishing: true, published: false };
    case 'successPublishing':
      return { ...state, publishing: false, published: true };
    case 'failPublishing':
      return { ...state, publishing: false, published: false };
    case 'navigateAfterPublishing':
      return { ...state, navigateToAfterPublishing };
    default:
      return state;
  }
};

export default function usePublish({ masterApiInstance }) {
  const [publish, dispatchPublish] = useReducer(publishReducer, {
    publishing: false,
    published: false,
    navigateToAfterPublishing: null,
  });

  async function savePassageToLearnosity({
    passageApiInstance,
    authoringApiInstance,
  }) {
    const questionWidgetData = authoringApiInstance.getWidget();

    if (passageApiInstance) {
      const { heading, content } = passageApiInstance.getWidget();

      // don't publish empty passages
      if (heading || content) {
        const passageWidgetData = passageApiInstance.getWidget();

        masterApiInstance.setWidget(passageWidgetData);
        await new Promise((resolve) =>
          masterApiInstance.once('widgetedit:widget:ready', resolve),
        );
        await saveAuthorInstance(masterApiInstance);
      }
    }

    masterApiInstance.setWidget(questionWidgetData);
    await new Promise((resolve) =>
      masterApiInstance.once('widgetedit:widget:ready', resolve),
    );
    await saveAuthorInstance(masterApiInstance);
  }

  async function publishQuestion({
    setShowValidation,
    authoringApiInstance,
    passageApiInstance,
    errorsOnQuestionValidation,
    scoringSectionErrors,
    validations,
    navigateToAfterPublishingPath,
    subjectInfo,
    initId,
    selectedTagsInfo,
    tags,
    saveQuestion,
    title,
    itemIdIfNotDuplicating,
    isFrq,
    scoreState,
    clearValidations,
    addQuestion,
    copyQuestion,
    replaceQuestion,
    replaceQuestionParams = {},
  }) {
    setShowValidation(false);
    const MAX_STRING_LENGTH = 255;
    // need to take into account validations.scoringSectionErrors in FRQ publish story
    if (
      errorsOnQuestionValidation === 0 &&
      scoringSectionErrors === 0 &&
      validations.hasName
    ) {
      dispatchPublish({ type: 'startPublishing' });

      try {
        const tagsToLrn = [
          {
            type: 'Subject',
            name: subjectInfo.name,
          },
          {
            type: 'created_by',
            name: initId,
          },
          ...(selectedTagsInfo && selectedTagsInfo.length
            ? selectedTagsInfo.map((obj) => {
                if (obj?.name?.length <= MAX_STRING_LENGTH) return obj;
                const cols = obj?.name?.split(':');
                if (cols?.length < 2) return obj;
                // join all after index zero
                return {
                  ...obj,
                  description: cols?.slice(1)?.join(':'),
                  name: cols && cols[0],
                };
              })
            : []),
        ];

        masterApiInstance.setItemTags(tagsToLrn);

        await savePassageToLearnosity({
          passageApiInstance,
          authoringApiInstance,
        });

        const learnosityItemReference = masterApiInstance.getItemReference();

        let rubrics = null;

        if (isFrq) {
          rubrics = await mapSectionsToQuestions(scoreState);
        }

        const responseData = await saveQuestion({
          variables: {
            subjectId: parseInt(subjectInfo.initId, 10),
            title,
            learnosityItemReference,
            type: isFrq
              ? 'questions/free_response'
              : 'questions/multiple_choice',
            tags: Object.keys(tags).reduce(
              (curr, next) => [...curr, ...tags[next]],
              [],
            ),
            ...rubrics,
            ...itemIdIfNotDuplicating,
            replaceQuestion: JSON.stringify(replaceQuestionParams),
          },
        });
        addQuestion(responseData.data.saveQuestion);
        if (copyQuestion) {
          toast("You've added this question to this assessment");
        } else if (replaceQuestion) {
          toast("You've updated this question on this assessment");
        } else {
          toast('The question was published', { hasCheckmark: true });
        }
        clearValidations();
        dispatchPublish({ type: 'successPublishing' });
        dispatchPublish({
          type: 'navigateAfterPublishing',
          payload: { navigateToAfterPublishing: navigateToAfterPublishingPath },
        });
      } catch (e) {
        log('error', e);
        notify.error('Something went wrong, please try again to publish');
        dispatchPublish({ type: 'failPublishing' });
      }
    } else {
      setShowValidation(true);
    }
  }

  return [publish, publishQuestion];
}
